// src/pages/finance/subledger/SubLedgerEntry.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const SubLedgerEntry = () => {
  const [rows, setRows] = useState([]);

  // ✅ dropdown accounts (searchable) from API
  const [subledgerAccounts, setSubledgerAccounts] = useState([]);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  // ✅ same role logic
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  // ✅ Filter state (same modal style as TransactionEntry)
  const [filter, setFilter] = useState({
    account_head_id: "",
    party_nature: "",
    party_name: "",
    pan: "",
    aadhaar: "",
    is_active: "",
  });

  /* ------------------------------------------------------------------ */
  /* API CALLS */
  /* ------------------------------------------------------------------ */

  const fetchPartyDetails = async () => {
    try {
      const resp = await axios.get(`${baseUrl}/subledger-party-details/`, {
        params: { school_id: userObj.school_id },
      });
      setRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.error("Error fetching party details:", e);
      setRows([]);
    }
  };

  const fetchSubledgerAccounts = async () => {
    try {
      setIsAccountsLoading(true);
      const resp = await axios.get(`${baseUrl}/subledger-accounts/`, {
        params: { school_id: userObj.school_id },
      });
      setSubledgerAccounts(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.error("Error fetching subledger accounts:", e);
      setSubledgerAccounts([]);
    } finally {
      setIsAccountsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchPartyDetails(), fetchSubledgerAccounts()]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------------ */
  /* DROPDOWN OPTIONS */
  /* ------------------------------------------------------------------ */

  const accountSelectOptions = useMemo(() => {
    return (subledgerAccounts || []).map((a) => ({
      value: a.account_head_id,
      label: a.account_head_name,
    }));
  }, [subledgerAccounts]);

  const selectedAccountOption = useMemo(() => {
    if (!filter.account_head_id) return null;
    return (
      accountSelectOptions.find((o) => String(o.value) === String(filter.account_head_id)) || null
    );
  }, [filter.account_head_id, accountSelectOptions]);

  /* ------------------------------------------------------------------ */
  /* EDIT / DELETE */
  /* ------------------------------------------------------------------ */

 const handleEditClick = (party_id) => {
  const record = rows.find((r) => String(r.party_id) === String(party_id));

  if (record) {
    navigate("/finance/addsubledger", {
      state: {
        SubLedgerData: {
          ...record,
          amount: record.amount ?? record.Amount ?? ""   // ✅ FIX HERE
        }
      }
    });
  }
};

  const handleDeleteClick = async (party_id) => {
    const confirmDelete = window.confirm("Are you sure you want to change the status?");
    if (!confirmDelete) return;

    try {
      await axios.post(`${baseUrl}/subledger-party-details/delete`, {
        party_id,
        school_id: userObj.school_id,
        modified_by: userObj.username || userObj.user_name || "admin",
      });

      toast.success("Record Set to InActive");
      fetchPartyDetails();
    } catch (e) {
      console.error("Delete error:", e);
      toast.error("Failed to delete record");
    }
  };

  /* ------------------------------------------------------------------ */
  /* GRID COLUMNS */
  /* ------------------------------------------------------------------ */

  const columns = useMemo(
    () => [
      {
        field: "account_head_name",
        headerName: "Account Head",
        flex: 1.6,
        headerAlign: "center",
        align: "left",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span>{params.value || "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "party_nature",
        headerName: "Party Nature",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span>{params.value || "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "party_name",
        headerName: "Party Name",
        flex: 1.4,
        headerAlign: "center",
        align: "left",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span>{params.value || "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "pan",
        headerName: "PAN",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span>{params.value || "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "aadhaar",
        headerName: "Aadhaar",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span>{params.value || "-"}</span>
          </Tooltip>
        ),
      },
      {
        field: "Amount",
        headerName: "Amount",
        flex: 1,
        headerAlign: "center",
        align: "right",
        renderCell: (params) => (
          <Tooltip title={params.value || "0"}>
            <span>{params.value || "0"}</span>
          </Tooltip>
        ),
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 2,
        headerAlign: "center",
        align: "left",
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <span className="text-truncate" style={{ maxWidth: 250 }}>
              {params.value || "-"}
            </span>
          </Tooltip>
        ),
      },
      {
        field: "is_active",
        headerName: "Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value === true ? "Active" : "InActive"}>
            <span>{params.value === true ? "Active" : "InActive"}</span>
          </Tooltip>
        ),
      },
      ...(canSubmit
        ? [
            {
              field: "actions",
              headerName: "Actions",
              flex: 1,
              headerAlign: "center",
              align: "center",
              sortable: false,
              renderCell: (params) => (
                <div className="tableActions">
                  <span
                    className="commonActionIcons"
                    onClick={() => handleEditClick(params.row.party_id)}
                  >
                    <MdEdit />
                  </span>
                  {/* <span
                    className="commonActionIcons"
                    onClick={() => handleDeleteClick(params.row.party_id)}
                  >
                    <MdDelete />
                  </span> */}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canSubmit, rows]
  );

  /* ------------------------------------------------------------------ */
  /* SEARCH (same as TransactionEntry) */
  /* ------------------------------------------------------------------ */

  const searchableColumns = [
    (r) => r.account_head_name,
    (r) => r.party_nature,
    (r) => r.party_name,
    (r) => r.pan,
    (r) => r.aadhaar,
    (r) => r.notes,
    (r) => r.Amount, // ✅ FIX HERE
    (r) => (r.is_active ? "Active" : "InActive"),
  ];

  const filteredRecords = (rows || []).filter((item) =>
    searchableColumns.some((selector) => {
      const value = selector(item);
      const s = String(value || "").toUpperCase().replace(/[-\s]+/g, "");
      const q = searchQuery.toUpperCase().replace(/[-\s]+/g, "");
      return s.includes(q);
    })
  );

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  /* ------------------------------------------------------------------ */
  /* FILTER SUBMIT (same modal behavior as TransactionEntry) */
  /* ------------------------------------------------------------------ */

  const handleFilterSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      school_id: userObj.school_id,
      account_head_id: filter.account_head_id ? Number(filter.account_head_id) : 0,
      party_nature: filter.party_nature || null,
      party_name: filter.party_name || null,
      pan: filter.pan || null,
      aadhaar: filter.aadhaar || null,
      is_active: filter.is_active === "" ? null : filter.is_active === "true",
    };

    try {
      const resp = await axios.post(`${baseUrl}/subledger-party-details/filter`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setRows(Array.isArray(resp.data) ? resp.data : []);
      setShowFilterModal(false);
    } catch (error) {
      console.error("Filter error:", error);
      toast.error("Failed to filter records");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setFilter({
      account_head_id: "",
      party_nature: "",
      party_name: "",
      pan: "",
      aadhaar: "",
      is_active: "",
    });
    setShowFilterModal(true);
    fetchPartyDetails();
  };

  /* ------------------------------------------------------------------ */
  /* UI */
  /* ------------------------------------------------------------------ */

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Sub-Ledger / Party Details</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/addsubledger")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords.length ? filteredRecords : []}
              columns={columns}
              getRowId={(row) => row.party_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      {/* ✅ Filter Modal (same classNames & layout as TransactionEntry) */}
      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modalBodyScrollable">
          <Form onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="account_head_id">
                    <Form.Label>Account</Form.Label>

                    <Select
                      inputId="account_head_id"
                      classNamePrefix="react-select"
                      placeholder={isAccountsLoading ? "Loading..." : "Select Account"}
                      isSearchable
                      isClearable
                      isLoading={isAccountsLoading}
                      options={accountSelectOptions}
                      value={selectedAccountOption}
                      onChange={(opt) =>
                        setFilter((f) => ({
                          ...f,
                          account_head_id: opt ? opt.value : "",
                        }))
                      }
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="party_nature">
                    <Form.Label>Party Nature</Form.Label>
                    <Form.Select
                      value={filter.party_nature}
                      onChange={(e) => setFilter((f) => ({ ...f, party_nature: e.target.value }))}
                    >
                      <option value="">Select</option>
                      <option value="Individual">Individual</option>
                      <option value="Company">Company</option>
                      <option value="Firm">Firm</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="party_name">
                    <Form.Label>Party Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Party Name"
                      value={filter.party_name}
                      onChange={(e) => setFilter((f) => ({ ...f, party_name: e.target.value }))}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="pan">
                    <Form.Label>PAN</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter PAN"
                      value={filter.pan}
                      onChange={(e) => setFilter((f) => ({ ...f, pan: e.target.value }))}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="aadhaar">
                    <Form.Label>Aadhaar</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Aadhaar"
                      value={filter.aadhaar}
                      onChange={(e) => setFilter((f) => ({ ...f, aadhaar: e.target.value }))}
                    />
                  </Form.Group>
                </div>
              </Col>

              
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              onClick={handleFilterSubmit}
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubLedgerEntry;